"use client";

import { useRouter } from "next/navigation";
import { useEffect, useTransition } from "react";

// Keeps an inbox page current without a socket: refreshes the server render
// every 15 seconds while the page is visible, including during composition,
// and, on a conversation its viewer owns, posts the "seen" mark with the
// newest message the page rendered (nothing changes on a GET).

const INTERVAL_MS = 15_000;

export type InboxRefresherProps = {
  /** The conversation and the seq of its newest rendered message, when the viewer owns it. */
  seen?: { conversationId: string; shown: string };
};

export function InboxRefresher({ seen }: InboxRefresherProps) {
  const router = useRouter();
  const [refreshing, startRefresh] = useTransition();

  useEffect(() => {
    if (refreshing) return;
    const timer = window.setInterval(() => {
      // Refresh merges the server payload while preserving the existing reply
      // field and its browser state. A draft must never hide incoming messages.
      if (document.visibilityState === "visible") startRefresh(() => router.refresh());
    }, INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [router, refreshing, startRefresh]);

  const conversationId = seen?.conversationId;
  const shown = seen?.shown;
  useEffect(() => {
    if (!conversationId || !shown || document.visibilityState !== "visible") return;
    const body = new URLSearchParams({ action: "seen", shown });
    void fetch(`/api/inbox/conversations/${encodeURIComponent(conversationId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
      credentials: "same-origin",
    }).catch(() => undefined);
  }, [conversationId, shown]);

  return null;
}
